import Box from '@mui/material/Box';
import {
	AutocompleteGroupedOption,
	UseAutocompleteRenderedOption
} from '@mui/material/useAutocomplete';
import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { useRecentHistory } from '../_hooks/useRecentHistory';
import {
	isAdvancedSearch,
	isProfileVisit,
	isRecentSearch,
	QuickSearchOption
} from '../_types/QuickSearch.types';
import { AdvancedSearchOption } from '../AdvancedSearchOption/AdvancedSearchOption';
import { ProfileCard } from '../ProfileCard/ProfileCard';

import { GroupHeader } from './GroupHeader/GroupHeader';
import { recentHistoryStyles as sx } from './RecentHistory.styles';
import { SearchHistory } from './SearchHistory/SearchHistory';

type Props = {
	groupedOptions: AutocompleteGroupedOption<QuickSearchOption>[];
	getOptionProps: (
		renderedOption: UseAutocompleteRenderedOption<QuickSearchOption>
	) => HTMLAttributes<HTMLLIElement> & {
		key: string;
	};
};

export const RecentHistory = ({ groupedOptions, getOptionProps }: Props) => {
	const { t } = useTranslation();
	const { clearHistory } = useRecentHistory();

	const handleHistoryClear = () => {
		clearHistory();
	};

	return (
		<>
			{groupedOptions.map((group, groupIndex) => (
				<Box key={group.key} component='ul' sx={sx.listGroup}>
					{(group.group === 'ProfileVisit' ||
						group.group === 'RecentSearch') && (
						<GroupHeader
							title={
								group.group === 'ProfileVisit'
									? t('Recently viewed')
									: t('Recently searched')
							}
							onClear={groupIndex === 0 ? handleHistoryClear : undefined}
						/>
					)}

					{group.options.map((option, index) => {
						const { key, ...optionProps } = getOptionProps({
							option,
							index: group.index + index
						});

						if (option.__typename === 'SearchAsYouTypeResult') return null;

						return (
							<Box key={key} component='li' {...optionProps} sx={sx.listOption}>
								{isProfileVisit(option) && (
									<ProfileCard
										profile={option.profile}
										view='compact'
										wrapChips={false}
									/>
								)}
								{isRecentSearch(option) && <SearchHistory search={option} />}
								{isAdvancedSearch(option) && <AdvancedSearchOption />}
							</Box>
						);
					})}
				</Box>
			))}
		</>
	);
};
